import { useCallback, useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import { tipGoalsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import type { TipGoal } from '../../types/tipGoals'
import ConfirmDialog from '../ConfirmDialog'
import TipGoalProgress, { dollars } from './TipGoalProgress'

/**
 * Monetization → Tip goals: a creator raises money toward a named goal.
 * Visitors contribute from the creator's profile, charged like a tip.
 * Creating one needs an approved application to monetize, like tiers.
 */

const MAX_TARGET_DOLLARS = 100_000

interface Props {
  creatorId: number
  /** Monetizing isn't approved: the form is replaced by this message. */
  blockedMessage?: string | null
}

const TipGoalsManager = ({ creatorId, blockedMessage }: Props) => {
  const [goals, setGoals] = useState<TipGoal[] | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [target, setTarget] = useState('')
  const [deadline, setDeadline] = useState('')
  const [saving, setSaving] = useState(false)
  const [ending, setEnding] = useState<TipGoal | null>(null)
  const [endingBusy, setEndingBusy] = useState(false)
  const formId = useId()

  const load = useCallback(() => {
    tipGoalsAPI
      .list(creatorId, true)
      .then(setGoals)
      .catch((e) => {
        setGoals([])
        toast.error(apiErrorMessage(e, 'Could not load your tip goals'))
      })
  }, [creatorId])

  useEffect(load, [load])

  const targetCents = Math.round((parseFloat(target) || 0) * 100)
  const today = new Date().toISOString().slice(0, 10)
  const valid = title.trim().length > 0 && targetCents >= 100 && targetCents <= MAX_TARGET_DOLLARS * 100

  const create = (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    setSaving(true)
    tipGoalsAPI
      .create({
        title: title.trim(),
        description: description.trim() || undefined,
        target_cents: targetCents,
        // The end of the chosen day, UTC.
        deadline: deadline ? `${deadline}T23:59:59Z` : undefined,
      })
      .then(() => {
        toast.success('Tip goal created')
        setTitle('')
        setDescription('')
        setTarget('')
        setDeadline('')
        load()
      })
      .catch((err) => toast.error(apiErrorMessage(err, 'Could not create the tip goal')))
      .finally(() => setSaving(false))
  }

  const end = () => {
    if (!ending) return
    setEndingBusy(true)
    tipGoalsAPI
      .end(ending.id)
      .then(() => {
        toast.success('Tip goal ended')
        setEnding(null)
        load()
      })
      .catch((err) => toast.error(apiErrorMessage(err, 'Could not end the tip goal')))
      .finally(() => setEndingBusy(false))
  }

  const open = (goals ?? []).filter((g) => g.is_active && !(g.deadline && new Date(g.deadline) <= new Date()))
  const past = (goals ?? []).filter((g) => !open.includes(g))

  return (
    <div className="space-y-6" data-testid="tip-goals-manager">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Tip goals</h2>
        <p className="mt-1 text-sm text-gray-600">
          Raise money toward something specific. Goals show on your profile with a progress bar, and anyone can
          contribute with a card. Contributions are paid out like tips.
        </p>
      </div>

      {blockedMessage ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="tip-goals-blocked">
          {blockedMessage}
        </p>
      ) : (
        <form onSubmit={create} className="rounded-lg border border-gray-200 bg-white p-4 space-y-3" aria-labelledby={`${formId}-h`}>
          <h3 id={`${formId}-h`} className="font-semibold text-gray-900">
            New goal
          </h3>
          <div>
            <label htmlFor={`${formId}-title`} className="block text-sm font-medium text-gray-700">
              What it's for
            </label>
            <input
              id={`${formId}-title`}
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. A second monitor for live sessions"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor={`${formId}-desc`} className="block text-sm font-medium text-gray-700">
              Details (optional)
            </label>
            <textarea
              id={`${formId}-desc`}
              value={description}
              maxLength={1000}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={`${formId}-target`} className="block text-sm font-medium text-gray-700">
                Target (USD)
              </label>
              <input
                id={`${formId}-target`}
                type="number"
                min="1"
                max={MAX_TARGET_DOLLARS}
                step="1"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor={`${formId}-deadline`} className="block text-sm font-medium text-gray-700">
                Ends (optional)
              </label>
              <input
                id={`${formId}-deadline`}
                type="date"
                min={today}
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={!valid || saving}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create goal'}
          </button>
        </form>
      )}

      {goals === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : goals.length === 0 ? (
        <p className="text-sm text-gray-500">No tip goals yet.</p>
      ) : (
        <>
          {open.length > 0 && (
            <section aria-label="Open goals" className="space-y-3">
              <h3 className="font-semibold text-gray-900">Open</h3>
              {open.map((g) => (
                <TipGoalProgress
                  key={g.id}
                  goal={g}
                  action={
                    <button
                      type="button"
                      onClick={() => setEnding(g)}
                      className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      End
                    </button>
                  }
                />
              ))}
            </section>
          )}
          {past.length > 0 && (
            <section aria-label="Past goals" className="space-y-3">
              <h3 className="font-semibold text-gray-900">Past</h3>
              {past.map((g) => (
                <TipGoalProgress key={g.id} goal={g} />
              ))}
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={ending !== null}
        title="End this tip goal?"
        message={
          ending
            ? `"${ending.title}" stops taking contributions. The ${dollars(ending.current_cents)} raised stays yours.`
            : ''
        }
        confirmLabel="End goal"
        danger
        confirming={endingBusy}
        onConfirm={end}
        onCancel={() => setEnding(null)}
      />
    </div>
  )
}

export default TipGoalsManager
